import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Images, ListTree } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCollections } from '@/hooks/useCollections';

export default function CollectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const collections = useCollections();
  const idx = collections.findIndex((c) => c.id === id);
  const c = collections[idx];

  if (!c) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="collection-not-found">
        <p className="font-serif text-3xl text-zinc-400">Collection not found.</p>
        <Link to="/collections" className="font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37] hover:text-[#F5E6AD]">
          ← All collections
        </Link>
      </div>
    );
  }

  const prev = collections[(idx - 1 + collections.length) % collections.length];
  const next = collections[(idx + 1) % collections.length];

  const inquire = (prefill) => navigate('/contact', { state: { prefill } });

  return (
    <div className="pt-[70px]" data-testid="collection-detail-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10">
        <Link
          to="/collections"
          data-testid="detail-back-link"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-[#D4AF37] transition-colors"
        >
          <ArrowLeft size={14} /> All Collections
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="absolute -inset-3 border border-[#D4AF37]/20 pointer-events-none" />
          <div className={`overflow-hidden ${c.image?.includes('/img/jsp-') ? 'bg-black' : 'bg-[#121215]'}`}>
            <img
              src={c.image}
              alt={`${c.name} collection featured product`}
              data-testid="detail-hero-image"
              className={`w-full h-[380px] sm:h-[480px] ${c.image?.includes('/img/jsp-') ? 'object-contain' : 'object-cover'}`}
            />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="kicker mb-4">{c.line}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight">
            {c.name}
          </h1>
          <p className="mt-6 text-zinc-400 font-light leading-relaxed max-w-md">{c.blurb}</p>
          <div className="mt-8 flex items-center gap-8">
            <div className="border-l border-[#D4AF37]/25 pl-4">
              <p className="font-serif text-3xl text-[#F5E6AD]" data-testid="detail-product-count">{c.items?.length || 0}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Products</p>
            </div>
            <div className="border-l border-[#D4AF37]/25 pl-4">
              <p className="font-serif text-3xl text-[#F5E6AD]">{c.pages?.length || 0}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Catalogue pages</p>
            </div>
          </div>
          <button
            data-testid="detail-inquire-btn"
            onClick={() => inquire({ collection: c.name, model: c.featured?.model, product: c.featured?.name })}
            className="mt-10 bg-[#D4AF37] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-[#F5E6AD] transition-colors duration-300"
          >
            Inquire About {c.name}
          </button>
        </motion.div>
      </section>

      {c.items?.some((it) => it.img) && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="detail-gallery">
          <div className="flex items-center gap-3 mb-8">
            <Images size={16} className="text-[#D4AF37]" />
            <h2 className="font-serif text-3xl sm:text-4xl font-light">
              The <span className="italic text-gold-gradient">gallery.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {c.items.filter((it) => it.img).map((it, i) => (
              <motion.figure
                key={it.model || i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                className="group"
                data-testid={`gallery-item-${it.model || i}`}
              >
                <div className={`relative aspect-square overflow-hidden border transition-colors duration-500 ${it.img.includes('/img/jsp-') ? 'bg-black border-[#D4AF37]/30 group-hover:border-[#D4AF37]/70' : 'bg-[#F4F3EF] border-[#D4AF37]/15 group-hover:border-[#D4AF37]/50'}`}>
                  <img
                    src={it.img}
                    alt={`${it.name} — ${it.model}`}
                    loading="lazy"
                    className="w-full h-full object-contain p-4 transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                </div>
                <figcaption className="mt-3 flex items-start justify-between gap-3">
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80">{it.model}</span>
                    <span className="block text-sm text-zinc-300 mt-0.5 leading-snug">{it.name}</span>
                  </span>
                  <button
                    data-testid={`gallery-inquire-${it.model || i}`}
                    onClick={() => inquire({ collection: c.name, model: it.model, product: it.name })}
                    className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 hover:text-[#D4AF37] transition-colors whitespace-nowrap mt-1"
                  >
                    Inquire
                  </button>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </section>
      )}

      {c.items?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="detail-product-list">
          <div className="flex items-center gap-3 mb-8">
            <ListTree size={16} className="text-[#D4AF37]" />
            <h2 className="font-serif text-3xl sm:text-4xl font-light">
              The range, <span className="italic text-gold-gradient">model by model.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-[#D4AF37]/10 border border-[#D4AF37]/10">
            {c.items.map((it, i) => (
              <motion.div
                key={it.model || i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: (i % 2) * 0.05 }}
                className="bg-[#0A0A0B] px-6 py-4 flex items-center justify-between gap-4 group hover:bg-[#121215] transition-colors"
                data-testid={`detail-item-${it.model || i}`}
              >
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80">{it.model || '—'}</p>
                  <p className="text-sm text-zinc-200 mt-0.5">{it.name}</p>
                </div>
                <button
                  data-testid={`detail-inquire-${it.model || i}`}
                  onClick={() => inquire({ collection: c.name, model: it.model, product: it.name })}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-[#D4AF37] hover:!text-[#F5E6AD] transition-colors whitespace-nowrap"
                >
                  Inquire Now
                </button>
              </motion.div>
            ))}
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            Prices shared on enquiry — April 2026 price list
          </p>
        </section>
      )}

      {c.pages?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="detail-catalogue-spread">
          <div className="flex items-center gap-3 mb-8">
            <Images size={16} className="text-[#D4AF37]" />
            <h2 className="font-serif text-3xl sm:text-4xl font-light">
              From the <span className="italic text-gold-gradient">catalogue.</span>
            </h2>
          </div>
          <div className={`grid gap-6 ${c.pages.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-2xl'}`}>
            {c.pages.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                className="relative group"
                data-testid={`detail-page-${i}`}
              >
                <div className="absolute -inset-2 border border-[#D4AF37]/15 pointer-events-none group-hover:border-[#D4AF37]/40 transition-colors duration-500" />
                <div className="overflow-hidden bg-[#121215]">
                  <img
                    src={src}
                    alt={`${c.name} catalogue page ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                  {c.name} — Page {i + 1} of {c.pages.length}
                </p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <nav className="border-t border-[#D4AF37]/15" data-testid="detail-pagination">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2">
          <Link
            to={`/collections/${prev.id}`}
            data-testid="detail-prev-link"
            className="group py-8 pr-6 border-r border-[#D4AF37]/15 flex items-center gap-4"
          >
            <ArrowLeft size={16} className="text-zinc-600 group-hover:text-[#D4AF37] group-hover:-translate-x-1 transition-all" />
            <span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Previous</span>
              <span className="font-serif text-xl sm:text-2xl text-zinc-300 group-hover:text-[#F5E6AD] transition-colors">{prev.name}</span>
            </span>
          </Link>
          <Link
            to={`/collections/${next.id}`}
            data-testid="detail-next-link"
            className="group py-8 pl-6 flex items-center justify-end gap-4 text-right"
          >
            <span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Next</span>
              <span className="font-serif text-xl sm:text-2xl text-zinc-300 group-hover:text-[#F5E6AD] transition-colors">{next.name}</span>
            </span>
            <ArrowRight size={16} className="text-zinc-600 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
          </Link>
        </div>
      </nav>
    </div>
  );
}
